// ---------------------------------------------------------------------------
// DETAIL_TABLE_CONFIG
//
// subtitle      — bold centered line under the serie/date heading
// textAbove     — markdown string shown above the table
//
// renderTable(rows, ctx) => JSX
//   ctx = { templateData }  (es. templateData.DATA_PLUS_4)
//
// secondary (optional):
//   headers / headersFromRowLength, textAbove, formatCell, renderTable
//
// NOTE / footnotes:
//   - desktop: dentro <tfoot> con classe obbligazioni-table__note
//   - mobile : dentro obbligazioni-table__note-mobile
//   I frammenti delle note sono definiti UNA volta e riusati nei due posti,
//   così il testo è sempre identico.
// ---------------------------------------------------------------------------
export const DETAIL_TABLE_CONFIG = {

  // -------------------------------------------------------------------------
  // PREMIA_SCADENZA
  // -------------------------------------------------------------------------
  premia_scadenza: {
    subtitle: null,
    textAbove: null,

    renderTable: (rows, ctx = {}) => {
      const dataPlus4 = ctx.templateData?.DATA_PLUS_4 ?? '-';

      // --- note (definite una volta, riusate in tfoot + mobile) ---
      const note1 = (
        <>(*) L&apos;indice è pubblicato su &quot;Il Sole 24ORE&quot;, pagina &quot;MERCATI AZIONARI MONDIALI&quot;, &quot;INDICI&quot;, &quot;Globali Area Euro: Euro Stoxx (50)&quot;.</>
      );
      const note2 = (
        <>La media finale dell&apos;Indice Euro Stoxx 50 è pari al valore medio delle medie aritmetiche semplici dei valori ufficiali di chiusura dell&apos;Indice rilevati per cinque giorni consecutivi a decorrere dal secondo lunedì, compreso, del mese antecedente il compimento del primo (I<sub>f</sub><sup>1</sup>), del secondo (I<sub>f</sub><sup>2</sup>), del terzo (I<sub>f</sub><sup>3</sup>), del quarto (I<sub>f</sub><sup>4</sup>) e del quinto anno (I<sub>f</sub><sup>5</sup>) di anzianità del buono fruttifero postale; le medie parziali (I<sub>f</sub><sup>i</sup>, i = 1, 2, 3, 4, 5) sono pubblicate non appena disponibili, mentre la pubblicazione della media finale avverrà nel mese di {dataPlus4}.</>
      );

      return (
        <section className="obbligazioni-table">
          <div className="obbligazioni-table__wrapper">
            <table className="obbligazioni-table__table">
              <thead>
                <tr>
                  <th colSpan={6} scope="colgroup">
                    <div className="obbligazioni-table__header">
                      Valori ufficiali di chiusura Euro Stoxx 50 (*)
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  const isHeaderRow = i % 2 === 0;
                  return (
                    <tr key={i}>
                      {row.map((cell, j) =>
                        isHeaderRow
                          ? <td key={j}><div className="font-weight-800">{cell ?? ''}</div></td>
                          : <td key={j}>{cell ?? ''}</td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={6} className="obbligazioni-table__note">{note1}</td>
                </tr>
                <tr>
                  <td colSpan={6} className="obbligazioni-table__note">{note2}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* ----- MOBILE CARDS ----- */}
          <div className="obbligazioni-table__mobile-cards">
            {Array.from({ length: Math.ceil(rows.length / 2) }).map((_, p) => {
              const headerRow = rows[p * 2] || [];
              const valueRow = rows[p * 2 + 1] || [];
              return (
                <div className="obbligazioni-table__mobile-card" key={p}>
                  <div className="obbligazioni-table__mobile-card-header">
                    <span className="obbligazioni-table__mobile-card-label">
                      Valori ufficiali di chiusura Euro Stoxx 50 (*)
                    </span>
                  </div>
                  {headerRow.map((label, j) => (
                    <div className="obbligazioni-table__mobile-card-item" key={j}>
                      <span className="obbligazioni-table__mobile-card-label">{label ?? ''}</span>
                      <span className="obbligazioni-table__mobile-card-value">{valueRow[j] ?? ''}</span>
                    </div>
                  ))}
                </div>
              );
            })}
            <div className="obbligazioni-table__note-mobile">
              <span>{note1}</span>
              <span>{note2}</span>
            </div>
          </div>
        </section>
      );
    },
    secondary: {
      headers: [
        '1ª Media Parziale',
        '2ª Media Parziale',
        '3ª Media Parziale',
        '4ª Media Parziale',
        '5ª Media Parziale',
        'Euro Stoxx 50 Media Finale',
      ],
      textAbove: null,
      formatCell: (value) => (value ?? ''),

      // Tabella secondaria (otherData): desktop + mobile cards
      renderTable: (rows) => {
        const headers = [
          '1ª Media Parziale',
          '2ª Media Parziale',
          '3ª Media Parziale',
          '4ª Media Parziale',
          '5ª Media Parziale',
          '6ª Media Parziale',
          'Euro Stoxx 50 Media Finale',
        ];
        const fmt = (v) => (v ?? '');

        return (
          <section className="obbligazioni-table">
            <div className="obbligazioni-table__wrapper">
              <table className="obbligazioni-table__table">
                <thead>
                  <tr>
                    {headers.map((h, j) => (
                      <th key={j} scope="col">
                        <div className="obbligazioni-table__header">{h}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i}>
                      {row.map((cell, j) => (
                        <td key={j}>{fmt(cell)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ----- MOBILE CARDS ----- */}
            <div className="obbligazioni-table__mobile-cards">
              {rows.map((row, i) => (
                <div className="obbligazioni-table__mobile-card" key={i}>
                  <div className="obbligazioni-table__mobile-card-header">
                    <span className="obbligazioni-table__mobile-card-label">
                      Euro Stoxx 50 — Medie parziali
                    </span>
                  </div>
                  {row.map((cell, j) => (
                    <div className="obbligazioni-table__mobile-card-item" key={j}>
                      <span className="obbligazioni-table__mobile-card-label">
                        {headers[j] ?? `Valore ${j + 1}`}
                      </span>
                      <span className="obbligazioni-table__mobile-card-value">{fmt(cell)}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </section>
        );
      },
    },
  },

  // -------------------------------------------------------------------------
  //  PREMIA
  // -------------------------------------------------------------------------
  premia: {
    subtitle: null,
    textAbove: null,

    renderTable: (rows) => {
      // --- note (definite una volta, riusate in tfoot + mobile) ---
      const note1 = <><strong>(*) Valori di riferimento</strong></>;
      const note2 = (
        <><strong>I<sub>0</sub></strong> è la media aritmetica dei valori ufficiali di chiusura dell&apos;Indice rilevati per cinque giorni consecutivi a decorrere dal secondo lunedì, compreso, del mese successivo a quello in cui cade la data di sottoscrizione del BFP<em>Premia</em>.</>
      );
      const note3 = (
        <><strong>I<sub>t</sub></strong> (t = 2, 3, 4, 5, 6, 7) è la media aritmetica dei valori ufficiali di chiusura dell&apos;Indice rilevati per cinque giorni consecutivi a decorrere dal secondo lunedì, compreso, del mese antecedente quello di compimento dell&apos;anno t dalla data di sottoscrizione del BFP<em>Premia</em>.</>
      );
      const note4 = (
        <><strong>(**)</strong> L&apos;indice è pubblicato su &quot;Il Sole 24ORE&quot;, pagina &quot;MERCATI AZIONARI MONDIALI&quot;, &quot;INDICI&quot;, &quot;Globali Area Euro: Euro Stoxx (50)&quot;.</>
      );

      return (
        <section className="obbligazioni-table">
          <div className="obbligazioni-table__wrapper">
            <table className="obbligazioni-table__table">
              <thead>
                <tr>
                  <th rowSpan={3} scope="col">
                    <div className="obbligazioni-table__header">Premio al termine del</div>
                  </th>
                  <th colSpan={4} scope="colgroup">
                    <div className="obbligazioni-table__header">Valori di riferimento (*)</div>
                  </th>
                  <th rowSpan={3} scope="col">
                    <div className="obbligazioni-table__header">Apprezzamento percentuale dell'Indice</div>
                  </th>
                  <th rowSpan={3} scope="col">
                    <div className="obbligazioni-table__header">Premio Eventuale in % del Valore Nominale</div>
                  </th>
                  <th rowSpan={3} scope="col">
                    <div className="obbligazioni-table__header">Premio corrisposto (Sì/No)</div>
                  </th>
                </tr>
                <tr>
                  <th colSpan={2} scope="colgroup">
                    <div className="obbligazioni-table__header">Media iniziale</div>
                  </th>
                  <th colSpan={2} scope="colgroup">
                    <div className="obbligazioni-table__header">Media finale</div>
                  </th>
                </tr>
                <tr>
                  <th scope="col"><div className="obbligazioni-table__header"><span>I<sub>in</sub></span></div></th>
                  <th scope="col"><div className="obbligazioni-table__header">giorni di rilevazione (**)</div></th>
                  <th scope="col"><div className="obbligazioni-table__header"><span>I<sub>fin</sub></span></div></th>
                  <th scope="col"><div className="obbligazioni-table__header">giorni di rilevazione</div></th>
                </tr>
              </thead>
              <tbody>
              {rows.map((row, i) => (
                  <tr key={i}>
                    {row.map((cell, j) => {
                      if (j === 2 || j === 5) return null;

                      if (j === 1 || j === 4) {
                        const next = j + 1;
                        return (
                            <td key={j}>
                              <span>i<sub>{(cell ?? '-')}</sub>={(row[next] ?? '-')}</span>
                            </td>
                        );
                      }

                      return <td key={j}>{cell ?? '-'}</td>;
                    })}
                  </tr>
              ))}
              </tbody>
              <tfoot>
                <tr><td colSpan={8} className="obbligazioni-table__note">{note1}</td></tr>
                <tr><td colSpan={8} className="obbligazioni-table__note">{note2}</td></tr>
                <tr><td colSpan={8} className="obbligazioni-table__note">{note3}</td></tr>
                <tr><td colSpan={8} className="obbligazioni-table__note">{note4}</td></tr>
              </tfoot>
            </table>
          </div>

          {/* ----- MOBILE CARDS ----- */}
          <div className="obbligazioni-table__mobile-cards">
            {rows.map((row, i) => {
              const isEmpty = (c) => c === null || c === undefined || c === '';
              const fmt = (c) => (c ?? '-');
              return (
              <div className="obbligazioni-table__mobile-card" key={i}>
                <div className="obbligazioni-table__mobile-card-header">
                  <span className="obbligazioni-table__mobile-card-label">
                    Premio al termine del: {fmt(row[0])}
                  </span>
                </div>

                <div className="obbligazioni-table__mobile-card-item">
                  <div className="obbligazioni-table__mobile-card-label-group">
                    <span className="obbligazioni-table__mobile-card-label">Valori di riferimento (*)</span>
                    <span className="obbligazioni-table__mobile-card-label">Media iniziale</span>
                    <span className="obbligazioni-table__mobile-card-label">I<sub>in</sub></span>
                  </div>
                  <span className="obbligazioni-table__mobile-card-value">
                    {isEmpty(row[1]) && isEmpty(row[2])
                      ? '-'
                      : <>i<sub>{row[1] ?? 'n'}</sub>={row[2] ?? '-'}</>
                    }
                  </span>
                </div>

                <div className="obbligazioni-table__mobile-card-item">
                  <div className="obbligazioni-table__mobile-card-label-group">
                    <span className="obbligazioni-table__mobile-card-label">Valori di riferimento (*)</span>
                    <span className="obbligazioni-table__mobile-card-label">Media iniziale</span>
                    <span className="obbligazioni-table__mobile-card-label">giorni di rilevazione (**)</span>
                  </div>
                  <span className="obbligazioni-table__mobile-card-value">{fmt(row[3])}</span>
                </div>

                <div className="obbligazioni-table__mobile-card-item">
                  <div className="obbligazioni-table__mobile-card-label-group">
                    <span className="obbligazioni-table__mobile-card-label">Valori di riferimento (*)</span>
                    <span className="obbligazioni-table__mobile-card-label">Media finale</span>
                    <span className="obbligazioni-table__mobile-card-label">I<sub>fin</sub></span>
                  </div>
                  <span className="obbligazioni-table__mobile-card-value">
                    {isEmpty(row[4]) && isEmpty(row[5])
                      ? '-'
                      : <>i<sub>{row[4] ?? 'n'}</sub>={row[5] ?? '-'}</>
                    }
                  </span>
                </div>

                <div className="obbligazioni-table__mobile-card-item">
                  <div className="obbligazioni-table__mobile-card-label-group">
                    <span className="obbligazioni-table__mobile-card-label">Valori di riferimento (*)</span>
                    <span className="obbligazioni-table__mobile-card-label">Media finale</span>
                    <span className="obbligazioni-table__mobile-card-label">giorni di rilevazione</span>
                  </div>
                  <span className="obbligazioni-table__mobile-card-value">{fmt(row[6])}</span>
                </div>

                <div className="obbligazioni-table__mobile-card-item">
                  <span className="obbligazioni-table__mobile-card-label">Apprezzamento percentuale dell&apos;Indice</span>
                  <span className="obbligazioni-table__mobile-card-value">{fmt(row[7])}</span>
                </div>

                <div className="obbligazioni-table__mobile-card-item">
                  <span className="obbligazioni-table__mobile-card-label">Premio Eventuale in % del Valore Nominale</span>
                  <span className="obbligazioni-table__mobile-card-value bold">{fmt(row[8])}</span>
                </div>

                <div className="obbligazioni-table__mobile-card-item">
                  <span className="obbligazioni-table__mobile-card-label">Premio corrisposto (Sì/No)</span>
                  <span className="obbligazioni-table__mobile-card-value bold">{fmt(row[9])}</span>
                </div>
              </div>
            )})}
            <div className="obbligazioni-table__note-mobile">
              <span>{note1}</span>
              <span>{note2}</span>
              <span>{note3}</span>
              <span>{note4}</span>
            </div>
          </div>
        </section>
      );
    }
  },

  // -------------------------------------------------------------------------
  // INFLAZIONE_E
  // -------------------------------------------------------------------------
  inflazione_e: {
    subtitle: 'Coefficienti per la determinazione del valore di rimborso lordo e netto al compimento dei 65 anni di età',
    textAbove: null,

    renderTable: (rows) => {
      const note1 = (
          <><strong>(*)</strong> Ai fini del calcolo dell&apos;inflazione europea di periodo e dei coefficienti indicati nella tabella, il &quot;Valore osservato&quot; del &quot;Valore Base dell&apos;Indice&quot; sarà eventualmente ribasato tenendo conto dei cambi di base comunicati dall&apos;Eurostat ed intercorsi fra la data di sottoscrizione del Buono e la data del compimento dei 65 anni di età del sottoscrittore. Tali variazioni non avranno alcun effetto sul rendimento dei Buoni.</>
      );
      return (
          <section
              className="obbligazioni-table obbligazioni-table--font-ultra-small obbligazioni-table--padding-small">
            <div className="obbligazioni-table__wrapper">
              <table className="obbligazioni-table__table">
                <thead>
                <tr>
                  <th colSpan={2} scope="colgroup">
                    <div className="obbligazioni-table__header">Età del sottoscrittore al momento della sottoscrizione
                      compresa
                    </div>
                  </th>
                  <th colSpan={2} scope="colgroup">
                    <div className="obbligazioni-table__header">Valore Base dell'Indice</div>
                  </th>
                  <th colSpan={2} scope="colgroup">
                    <div className="obbligazioni-table__header">Valore a 65 anni dell'indice</div>
                  </th>
                  <th rowSpan={2} scope="col">
                    <div className="obbligazioni-table__header">Inflazione di periodo</div>
                  </th>
                  <th rowSpan={2} scope="col">
                    <div className="obbligazioni-table__header">Tasso di partecipazione</div>
                  </th>
                  <th rowSpan={2} scope="col">
                    <div className="obbligazioni-table__header">Coefficiente di rivalutazione del capitale</div>
                  </th>
                  <th rowSpan={2} scope="col">
                    <div className="obbligazioni-table__header">Coefficiente minimo garantito</div>
                  </th>
                  <th rowSpan={2} scope="col">
                    <div className="obbligazioni-table__header">Rivaluta (SI/NO)</div>
                  </th>
                  <th rowSpan={2} scope="col">
                    <div className="obbligazioni-table__header">Coefficiente finale lordo a 65 anni</div>
                  </th>
                  <th rowSpan={2} scope="col">
                    <div className="obbligazioni-table__header">Coefficiente finale netto a 65 anni</div>
                  </th>
                </tr>
                <tr>
                  <th scope="col">
                    <div className="obbligazioni-table__header">dal giorno del compimento dei</div>
                  </th>
                  <th scope="col">
                    <div className="obbligazioni-table__header">al giorno precedente il compimento dei</div>
                  </th>
                  <th scope="col">
                    <div className="obbligazioni-table__header">Mese di rilevazione</div>
                  </th>
                  <th scope="col">
                    <div className="obbligazioni-table__header">Valore osservato (*)</div>
                  </th>
                  <th scope="col">
                    <div className="obbligazioni-table__header">Mese di rilevazione</div>
                  </th>
                  <th scope="col">
                    <div className="obbligazioni-table__header">Valore osservato</div>
                  </th>
                </tr>
                </thead>
                <tbody>
                {rows.map((row, i) => (
                    <tr key={i}>
                      {row.map((cell, j) => (
                          <td key={j}>{cell === 0 || cell === '0' ? '-' : (cell ?? '-')}</td>
                      ))}
                    </tr>
                ))}
                </tbody>
                <tfoot>
                <tr>
                  <td colSpan={13} className="obbligazioni-table__note">{note1}</td>
                </tr>
                </tfoot>
              </table>
            </div>

            {/* ----- MOBILE CARDS ----- */}
            <div className="obbligazioni-table__mobile-cards">
              {
                rows.map((row, i) => {
                  const fmt = (c) => (c === 0 || c === '0' ? '-' : (c ?? '-'));
                  return (
                      <div className="obbligazioni-table__mobile-card" key={i}>
                        <div className="obbligazioni-table__mobile-card-header">
                  <span className="obbligazioni-table__mobile-card-label">
                    Età del sottoscrittore: dal {fmt(row[0])} al {fmt(row[1])}
                  </span>
                        </div>

                        <div className="obbligazioni-table__mobile-card-item">
                          <div className="obbligazioni-table__mobile-card-label-group">
                            <span className="obbligazioni-table__mobile-card-label">Età del sottoscrittore</span>
                            <span className="obbligazioni-table__mobile-card-label">dal giorno del compimento dei</span>
                          </div>
                          <span className="obbligazioni-table__mobile-card-value">{fmt(row[0])}</span>
                        </div>

                        <div className="obbligazioni-table__mobile-card-item">
                          <div className="obbligazioni-table__mobile-card-label-group">
                            <span className="obbligazioni-table__mobile-card-label">Età del sottoscrittore</span>
                            <span
                                className="obbligazioni-table__mobile-card-label">al giorno precedente il compimento dei</span>
                          </div>
                          <span className="obbligazioni-table__mobile-card-value">{fmt(row[1])}</span>
                        </div>

                        <div className="obbligazioni-table__mobile-card-item">
                          <div className="obbligazioni-table__mobile-card-label-group">
                            <span className="obbligazioni-table__mobile-card-label">Valore Base dell&apos;Indice</span>
                            <span className="obbligazioni-table__mobile-card-label">Mese di rilevazione</span>
                          </div>
                          <span className="obbligazioni-table__mobile-card-value">{fmt(row[2])}</span>
                        </div>

                        <div className="obbligazioni-table__mobile-card-item">
                          <div className="obbligazioni-table__mobile-card-label-group">
                            <span className="obbligazioni-table__mobile-card-label">Valore Base dell&apos;Indice</span>
                            <span className="obbligazioni-table__mobile-card-label">Valore osservato</span>
                          </div>
                          <span className="obbligazioni-table__mobile-card-value">{fmt(row[3])}</span>
                        </div>

                        <div className="obbligazioni-table__mobile-card-item">
                          <div className="obbligazioni-table__mobile-card-label-group">
                          <span
                              className="obbligazioni-table__mobile-card-label">Valore a 65 anni dell&apos;indice</span>
                            <span className="obbligazioni-table__mobile-card-label">Mese di rilevazione</span>
                          </div>
                          <span className="obbligazioni-table__mobile-card-value">{fmt(row[4])}</span>
                        </div>

                        <div className="obbligazioni-table__mobile-card-item">
                          <div className="obbligazioni-table__mobile-card-label-group">
                          <span
                              className="obbligazioni-table__mobile-card-label">Valore a 65 anni dell&apos;indice</span>
                            <span className="obbligazioni-table__mobile-card-label">Valore osservato</span>
                          </div>
                          <span className="obbligazioni-table__mobile-card-value">{fmt(row[5])}</span>
                        </div>

                        <div className="obbligazioni-table__mobile-card-item">
                          <span className="obbligazioni-table__mobile-card-label">Inflazione di periodo</span>
                          <span className="obbligazioni-table__mobile-card-value">{fmt(row[6])}</span>
                        </div>

                        <div className="obbligazioni-table__mobile-card-item">
                          <span className="obbligazioni-table__mobile-card-label">Tasso di partecipazione</span>
                          <span className="obbligazioni-table__mobile-card-value">{fmt(row[7])}</span>
                        </div>

                        <div className="obbligazioni-table__mobile-card-item">
                    <span
                        className="obbligazioni-table__mobile-card-label">Coefficiente di rivalutazione del capitale</span>
                          <span className="obbligazioni-table__mobile-card-value">{fmt(row[8])}</span>
                        </div>

                        <div className="obbligazioni-table__mobile-card-item">
                          <span className="obbligazioni-table__mobile-card-label">Coefficiente minimo garantito</span>
                          <span className="obbligazioni-table__mobile-card-value">{fmt(row[9])}</span>
                        </div>

                        <div className="obbligazioni-table__mobile-card-item">
                          <span className="obbligazioni-table__mobile-card-label">Rivaluta (SI/NO)</span>
                          <span className="obbligazioni-table__mobile-card-value">{fmt(row[10])}</span>
                        </div>

                        <div className="obbligazioni-table__mobile-card-item">
                        <span
                            className="obbligazioni-table__mobile-card-label">Coefficiente finale lordo a 65 anni</span>
                          <span className="obbligazioni-table__mobile-card-value bold">{fmt(row[11])}</span>
                        </div>

                        <div className="obbligazioni-table__mobile-card-item">
                        <span
                            className="obbligazioni-table__mobile-card-label">Coefficiente finale netto a 65 anni</span>
                          <span className="obbligazioni-table__mobile-card-value bold">{fmt(row[12])}</span>
                        </div>
                      </div>
                  );
                })}
              <div className="obbligazioni-table__note-mobile">
                <span>{note1}</span>
              </div>
            </div>

          </section>
      );
    }
  },

  // -------------------------------------------------------------------------
  // INFLAZIONE_I
  // -------------------------------------------------------------------------
  inflazione_i: {
    subtitle:  (rows) => `INDICE ISTAT FOI ex-TABACCHI Valore base (FOI-3) (*): ${rows?.[0]?.[7] ?? '-'}`,
    textAbove: null,

    renderTable: (rows) => (
      <section className="obbligazioni-table">
        <div className="obbligazioni-table__wrapper">
          <table className="obbligazioni-table__table">
            <thead>
            <tr>
              <th colSpan={6} scope="colgroup">
                <div className="obbligazioni-table__header">Coefficienti per la determinazione del Valore di rimborso</div>
              </th>
            </tr>
            <tr>
              <th scope="col">
                <div className="obbligazioni-table__header">Mese di rilevazione</div>
              </th>
              <th scope="col">
                <div className="obbligazioni-table__header">Valore osservato indice FOI (**)</div>
              </th>
              <th scope="col">
                <div className="obbligazioni-table__header">Bimestre compiuto</div>
              </th>
              <th scope="col">
                <div className="obbligazioni-table__header">Coefficiente di indicizzazione</div>
              </th>
              <th scope="col">
                <div className="obbligazioni-table__header">Coefficiente complessivo lordo</div>
              </th>
              <th scope="col">
                <div className="obbligazioni-table__header">Coefficiente complessivo netto</div>
              </th>
            </tr>
            </thead>
            <tbody>
            {rows.map((row, i) => (
                <tr key={i}>
                  {row.map((cell, j) => {
                        if (j === 4 || j === 7)
                          return null;
                        if (j === 3) {
                          const next = j + 1;
                          return (
                              <td key={j}>
                                <span>CI<sub>{cell === 0 || cell === '0' ? '-' : (cell ?? '-')}</sub>={(row[next] ?? '-')}</span>
                              </td>
                          );
                        }
                        return <td key={j}>{cell === 0 || cell === '0' ? '-' : (cell ?? '-')}</td>;

                      })}
                </tr>
            ))}
            </tbody>
          </table>
        </div>

          {/* ----- MOBILE CARDS ----- */}
          <div className="obbligazioni-table__mobile-cards">
            {rows.map((row, i) => {
              const fmt = (c) => (c ?? '-');
              const isEmpty = (c) => c === null || c === undefined || c === '';
              return (
              <div className="obbligazioni-table__mobile-card" key={i}>
                <div className="obbligazioni-table__mobile-card-header">
                  <span className="obbligazioni-table__mobile-card-label">
                    Coefficienti per la determinazione del Valore di rimborso
                  </span>
                </div>

                <div className="obbligazioni-table__mobile-card-item">
                  <span className="obbligazioni-table__mobile-card-label">Mese di rilevazione</span>
                  <span className="obbligazioni-table__mobile-card-value">{fmt(row[0])}</span>
                </div>

                <div className="obbligazioni-table__mobile-card-item">
                  <span className="obbligazioni-table__mobile-card-label">Valore osservato indice FOI (**)</span>
                  <span className="obbligazioni-table__mobile-card-value">{fmt(row[1])}</span>
                </div>

                <div className="obbligazioni-table__mobile-card-item">
                  <span className="obbligazioni-table__mobile-card-label">Bimestre compiuto</span>
                  <span className="obbligazioni-table__mobile-card-value bold">{fmt(row[2])}</span>
                </div>

                <div className="obbligazioni-table__mobile-card-item">
                  <span className="obbligazioni-table__mobile-card-label">Coefficiente di indicizzazione</span>
                  <span className="obbligazioni-table__mobile-card-value bold">
                    {isEmpty(row[3]) && isEmpty(row[4])
                      ? '-'
                      : <>CI<sub>{row[3] ?? 'i'}</sub>={row[4] ?? '-'}</>
                    }
                  </span>
                </div>

                <div className="obbligazioni-table__mobile-card-item">
                  <span className="obbligazioni-table__mobile-card-label">Coefficiente complessivo lordo</span>
                  <span className="obbligazioni-table__mobile-card-value">{fmt(row[5])}</span>
                </div>

                <div className="obbligazioni-table__mobile-card-item">
                  <span className="obbligazioni-table__mobile-card-label">Coefficiente complessivo netto</span>
                  <span className="obbligazioni-table__mobile-card-value bold">{fmt(row[6])}</span>
                </div>
              </div>
            )})}
          </div>
      </section>
    )
  },

  // -------------------------------------------------------------------------
  // ESEGIBILI
  // -------------------------------------------------------------------------
  esegibili: {
    subtitle: 'Premio a scadenza',
    textAbove: null,

    renderTable: (rows) => {
      const fmt = (c) => (c === 0 || c === '0' ? 'i=0' : (c ?? '-'));

      // --- note (definite una volta, riusate in tfoot + mobile) ---
      const note1 = <><strong>(*) Valori di riferimento</strong></>;
      const note2 = (
        <><strong>K</strong> è il valore iniziale (strike) dell&apos;indice, rilevato il secondo mercoledì del mese successivo alla sottoscrizione.</>
      );
      const note3 = (
        <><strong>S<sub>T</sub></strong> è il valore finale dell&apos;indice, rilevato il secondo mercoledì del mese precedente la scadenza.</>
      );

      return (
        <section className="obbligazioni-table">
          <div className="obbligazioni-table__wrapper">
            <table className="obbligazioni-table__table">
              <thead>
                <tr>
                  <th colSpan={4} scope="colgroup">
                    <div className="obbligazioni-table__header">Valori di riferimento (*)</div>
                  </th>
                  <th rowSpan={3} scope="col">
                    <div className="obbligazioni-table__header">Apprezzamento dell'indice</div>
                  </th>
                  <th rowSpan={3} scope="col">
                    <div className="obbligazioni-table__header">Tasso di partecipazione (P)</div>
                  </th>
                  <th rowSpan={3} scope="col">
                    <div className="obbligazioni-table__header">Premio Eventuale in % del Valore Nominale</div>
                  </th>
                  <th rowSpan={3} scope="col">
                    <div className="obbligazioni-table__header">Premio corrisposto (Sì/No)</div>
                  </th>
                </tr>
                <tr>
                  <th colSpan={2} scope="colgroup">
                    <div className="obbligazioni-table__header">Valore iniziale</div>
                  </th>
                  <th colSpan={2} scope="colgroup">
                    <div className="obbligazioni-table__header">Valore finale</div>
                  </th>
                </tr>
                <tr>
                  <th scope="col"><div className="obbligazioni-table__header">K</div></th>
                  <th scope="col"><div className="obbligazioni-table__header">Giorno di rilevazione</div></th>
                  <th scope="col"><div className="obbligazioni-table__header"><span>S<sub>T</sub></span></div></th>
                  <th scope="col"><div className="obbligazioni-table__header">Giorno di rilevazione</div></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i}>
                    {row.map((cell, j) => (
                      <td key={j}>{fmt(cell)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr><td colSpan={8} className="obbligazioni-table__note">{note1}</td></tr>
                <tr><td colSpan={8} className="obbligazioni-table__note">{note2}</td></tr>
                <tr><td colSpan={8} className="obbligazioni-table__note">{note3}</td></tr>
              </tfoot>
            </table>
          </div>

          {/* ----- MOBILE CARDS ----- */}
          <div className="obbligazioni-table__mobile-cards">
            {rows.map((row, i) => (
              <div className="obbligazioni-table__mobile-card" key={i}>
                <div className="obbligazioni-table__mobile-card-header">
                  <span className="obbligazioni-table__mobile-card-label">
                    Valore iniziale (K): {fmt(row[0])}
                  </span>
                </div>

                <div className="obbligazioni-table__mobile-card-item">
                  <div className="obbligazioni-table__mobile-card-label-group">
                    <span className="obbligazioni-table__mobile-card-label">Valori di riferimento (*)</span>
                    <span className="obbligazioni-table__mobile-card-label">Valore iniziale</span>
                    <span className="obbligazioni-table__mobile-card-label">K</span>
                  </div>
                  <span className="obbligazioni-table__mobile-card-value">{fmt(row[0])}</span>
                </div>

                <div className="obbligazioni-table__mobile-card-item">
                  <div className="obbligazioni-table__mobile-card-label-group">
                    <span className="obbligazioni-table__mobile-card-label">Valori di riferimento (*)</span>
                    <span className="obbligazioni-table__mobile-card-label">Valore iniziale</span>
                    <span className="obbligazioni-table__mobile-card-label">Giorno di rilevazione</span>
                  </div>
                  <span className="obbligazioni-table__mobile-card-value">{fmt(row[1])}</span>
                </div>

                <div className="obbligazioni-table__mobile-card-item">
                  <div className="obbligazioni-table__mobile-card-label-group">
                    <span className="obbligazioni-table__mobile-card-label">Valori di riferimento (*)</span>
                    <span className="obbligazioni-table__mobile-card-label">Valore finale</span>
                    <span className="obbligazioni-table__mobile-card-label">S<sub>T</sub></span>
                  </div>
                  <span className="obbligazioni-table__mobile-card-value">{fmt(row[2])}</span>
                </div>

                <div className="obbligazioni-table__mobile-card-item">
                  <div className="obbligazioni-table__mobile-card-label-group">
                    <span className="obbligazioni-table__mobile-card-label">Valori di riferimento (*)</span>
                    <span className="obbligazioni-table__mobile-card-label">Valore finale</span>
                    <span className="obbligazioni-table__mobile-card-label">Giorno di rilevazione</span>
                  </div>
                  <span className="obbligazioni-table__mobile-card-value">{fmt(row[3])}</span>
                </div>

                <div className="obbligazioni-table__mobile-card-item">
                  <span className="obbligazioni-table__mobile-card-label">Apprezzamento dell&apos;indice</span>
                  <span className="obbligazioni-table__mobile-card-value">{fmt(row[4])}</span>
                </div>

                <div className="obbligazioni-table__mobile-card-item">
                  <span className="obbligazioni-table__mobile-card-label">Tasso di partecipazione (P)</span>
                  <span className="obbligazioni-table__mobile-card-value">{fmt(row[5])}</span>
                </div>

                <div className="obbligazioni-table__mobile-card-item">
                  <span className="obbligazioni-table__mobile-card-label">Premio Eventuale in % del Valore Nominale</span>
                  <span className="obbligazioni-table__mobile-card-value bold">{fmt(row[6])}</span>
                </div>

                <div className="obbligazioni-table__mobile-card-item">
                  <span className="obbligazioni-table__mobile-card-label">Premio corrisposto (Sì/No)</span>
                  <span className="obbligazioni-table__mobile-card-value bold">{fmt(row[7])}</span>
                </div>
              </div>
            ))}
            <div className="obbligazioni-table__note-mobile">
              <span>{note1}</span>
              <span>{note2}</span>
              <span>{note3}</span>
            </div>
          </div>
        </section>
      );
    }
  },

  // -------------------------------------------------------------------------
  // DEFAULT fallback
  // -------------------------------------------------------------------------
  default: {
    subtitle: null,
    textAbove: 'Di seguito il dettaglio dei dati per la serie selezionata.',
    headersFromRowLength: true,
    formatCell: (value) => (value === 0 || value === '0' ? 'i=0' : (value ?? '')),
  },
};